import { ProductType, RawGreeksDataMap, RawStatsDataMap } from '../../types';
import { fetchRawFormattedData, getFilteredGreeksData } from 'util/iVol';
import useLog from '../useLog';
import { useCallback } from 'react';
import useAuth from 'hooks/auth/useAuth';

interface UseImpliedVolatilityProps {
  bypassAccessCheck?: boolean;
}

const useImpliedVolatility = ({
  bypassAccessCheck = false,
}: UseImpliedVolatilityProps = {}) => {
  const { logError } = useLog('useImpliedVolatility');
  const { hasAccessToProduct } = useAuth();
  const hasAccess =
    bypassAccessCheck || hasAccessToProduct(ProductType.IMPLIED_VOL);

  const getCurrentGreeksData = useCallback(
    async (ticker: string): Promise<RawGreeksDataMap | null> => {
      try {
        const rawData: RawGreeksDataMap = await fetchRawFormattedData(
          hasAccess
            ? `v2/latest_greeks?sym=${encodeURIComponent(ticker)}`
            : `v2/free_latest_greeks?sym=${encodeURIComponent(ticker)}`,
        );

        const updatedRawData: RawGreeksDataMap = getFilteredGreeksData(rawData);

        return updatedRawData;
      } catch (error) {
        logError(error, 'getCurrentGreeksData');
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasAccess],
  );

  const getDailyGreeksData = useCallback(
    async (
      date: string,
      ticker: string,
      isMktOpen?: boolean,
    ): Promise<RawGreeksDataMap | null> => {
      try {
        const mktClose = (isMktOpen ?? false) ? 0 : 1;
        const rawData: RawGreeksDataMap = await fetchRawFormattedData(
          hasAccess
            ? `v2/daily_greeks?sym=${encodeURIComponent(ticker)}&date=${date}&mkt_close=${mktClose}`
            : `v2/free_daily_greeks?sym=${encodeURIComponent(ticker)}&date=${date}&mkt_close=${mktClose}`,
        );

        // filter out any expirations before given trade "date"
        const updatedRawData: RawGreeksDataMap = getFilteredGreeksData(
          rawData,
          date,
        );

        return updatedRawData;
      } catch (error) {
        logError(error, 'getDailyGreeksData');
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasAccess],
  );

  const getStatisticsData = useCallback(
    async (ticker: string, date?: string): Promise<RawStatsDataMap | null> => {
      try {
        return await fetchRawFormattedData(
          `v1/${
            !hasAccess ? 'free_iv_stats' : 'iv_stats'
          }?sym=${encodeURIComponent(ticker)}${date != null ? `&date=${date}` : ''}`,
        );
      } catch (error) {
        logError(error, 'getStatisticsData');
        return null;
      }
    },
    [hasAccess, logError],
  );

  return {
    getCurrentGreeksData,
    getDailyGreeksData,
    getStatisticsData,
  };
};

export default useImpliedVolatility;
